import { PAYMENTS_INVALIDATION_DOMAIN, TRANSACTION_TABLE_NAME } from '@rentguru/commons-utils';
import { useQueryClient } from '@tanstack/react-query';
import { onSubscriptionsReconnected } from '@up2rent/graphql';
import debounce from 'lodash/debounce';
import { useEffect, useMemo } from 'react';

import { invalidatePayments } from './paymentsInvalidation';
import { useInvalidationBus } from './useInvalidationBus';
import { useUser } from './UserContext';

const PAYMENTS_INVALIDATION_TRAILING_IN_MS = 1000;
const PAYMENTS_INVALIDATION_MAX_WAIT_IN_MS = 5000;

interface PaymentsInvalidationListenerProps {
  onRejected: () => void;
}

const PaymentsInvalidationListener = ({ onRejected }: PaymentsInvalidationListenerProps) => {
  const queryClient = useQueryClient();
  const { clientId } = useUser();

  const refreshPayments = useMemo(
    () =>
      debounce(() => invalidatePayments(queryClient, clientId), PAYMENTS_INVALIDATION_TRAILING_IN_MS, {
        maxWait: PAYMENTS_INVALIDATION_MAX_WAIT_IN_MS,
      }),
    [clientId, queryClient]
  );

  useEffect(() => {
    refreshPayments();
    return () => refreshPayments.cancel();
  }, [refreshPayments]);

  useInvalidationBus(
    (message) => {
      if (message.domains.includes(PAYMENTS_INVALIDATION_DOMAIN)) {
        refreshPayments();
      }
    },
    {
      onLegacyRefetch: (table) => {
        if (table === TRANSACTION_TABLE_NAME) {
          refreshPayments();
        }
      },
      onRejected,
    }
  );

  useEffect(() => onSubscriptionsReconnected(refreshPayments), [refreshPayments]);

  return null;
};

export default PaymentsInvalidationListener;
